import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { FolderOpen, Plus, Loader2 } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { aws as awsClient } from "@/aws/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { readAllRows } from "@/lib/read-all-rows";

export const Route = createFileRoute("/projects/")({
  head: () => ({
    meta: [
      { title: "Projects — AskBell" },
      { name: "description", content: "Organize chats and knowledge bases by project." },
    ],
  }),
  component: ProjectsIndexPage,
});

type ProjectRow = {
  id: string;
  name: string;
  description: string | null;
  updated_at: string;
};

function ProjectsIndexPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<ProjectRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!user) { setRows([]); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    readAllRows<ProjectRow>((from,to)=>awsClient
      .from("projects")
      .select("id, name, description, updated_at")
      .order("updated_at", { ascending: false })
      .order("id", { ascending: true }).range(from,to),()=>!cancelled)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { setLoadError(error.message); }
        else setRows((data ?? []) as ProjectRow[]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user, reload]);

  const handleCreate = async () => {
    if (!name.trim() || !user || creating) return;
    setCreating(true);
    const { data, error } = await awsClient
      .from("projects")
      .insert({ user_id: user.id, name: name.trim() })
      .select("id")
      .single();
    setCreating(false);
    if (error || !data) {
      toast.error("Couldn't create project.");
      return;
    }
    setName("");
    navigate({ to: "/projects/$projectId", params: { projectId: data.id } });
  };

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <h1 className="text-ui-2xl font-bold tracking-tight text-foreground">Projects</h1>
            <p className="text-ui-base text-muted-foreground mt-1">
              Group chats around a topic and give them shared knowledge & instructions.
            </p>
          </div>

          <div className="rounded-ui-md border border-border bg-card p-4 mb-6">
            <h2 className="text-ui-base font-bold mb-2">Create a project</h2>
            <div className="flex gap-2">
              <Input
                aria-label="Project name"
                maxLength={160}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCreate(); } }}
                placeholder="e.g. Acme RFP 2025"
                disabled={creating}
              />
              <Button onClick={handleCreate} disabled={!name.trim() || creating} className="gap-1.5">
                <Plus className="h-4 w-4" />
                Create
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : loadError ? (
            <div role="alert" className="rounded-ui-md border border-destructive/30 bg-destructive/5 p-5">
              <p className="text-ui-base">Couldn't load projects. {loadError}</p>
              <Button variant="outline" className="mt-3" onClick={()=>setReload(value=>value+1)}>Try again</Button>
            </div>
          ) : rows && rows.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <FolderOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-ui-base">No projects yet.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {(rows ?? []).map((p) => (
                <li key={p.id}>
                  <Link
                    to="/projects/$projectId"
                    params={{ projectId: p.id }}
                    className="block rounded-ui-md border border-border bg-card hover:border-primary/40 hover:bg-secondary/40 transition-colors px-4 py-3"
                  >
                    <div className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4 text-primary" />
                      <span className="font-bold text-foreground">{p.name}</span>
                    </div>
                    {p.description && (
                      <p className="text-ui-sm text-muted-foreground mt-1 line-clamp-2 ml-6">{p.description}</p>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
